import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import useOrderedCustomRequests from '../useOrderedCustomRequests';

export default function useCustomTaxRequests(vehicleId: string) {
  const queryKey = getQueryKey(
    api.onboardingCustomRequest.listTaxCustomRequests,
    { vehicleId },
    'query',
  );

  const { data: customTaxRequests, isLoading: isLoadingCustomTaxRequests } =
    api.onboardingCustomRequest.listTaxCustomRequests.useQuery({
      vehicleId,
    });

  const { orderedCustomRequests, setCustomRequestsOrder } =
    useOrderedCustomRequests(customTaxRequests ?? []);

  const { mutate: addRequest, isPending: isAddingRequest } =
    api.onboardingCustomRequest.addTaxCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request added successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: updateRequest, isPending: isUpdatingRequest } =
    api.onboardingCustomRequest.updateTaxCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request updated successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: deleteRequest, isPending: isDeletingRequest } =
    api.onboardingCustomRequest.deleteTaxCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request deleted successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: reorderTaxCustomRequests } =
    api.onboardingCustomRequest.reorderTaxCustomRequests.useMutation(
      useHandleTrpcMutation()({
        invalidations: [queryKey],
        onSuccess: () => {
          setCustomRequestsOrder([]);
        },
      }),
    );

  const handleCustomRequestsOrderChange = (orderedItemIds: Array<string>) => {
    setCustomRequestsOrder(orderedItemIds);
    reorderTaxCustomRequests({
      vehicleId,
      orderedItemIds,
    });
  };

  const isLoading =
    isLoadingCustomTaxRequests ||
    isAddingRequest ||
    isUpdatingRequest ||
    isDeletingRequest;

  return {
    customTaxRequests: orderedCustomRequests,
    handleCustomRequestsOrderChange,
    addCustomTaxRequest: addRequest,
    deleteCustomTaxRequest: deleteRequest,
    updateCustomTaxRequest: updateRequest,
    isLoading,
  };
}
